import { api } from './client';

export type InstallationResolveStatus = 'registered' | 'no_access' | 'unregistered';
export type RegistrationRequestStatus = 'PENDING' | 'APPROVED' | 'CONSUMED' | 'EXPIRED' | 'REJECTED' | 'REVOKED';
export type GuestDownloadRequestStatus = 'PENDING' | 'APPROVED' | 'EXPIRED' | 'REVOKED';

export interface InstallationIdentityRecord {
  type: string;
  version: number;
  appId: string;
  installationId: string;
  stationId?: string;
  softwareName?: string;
  softwareVersion?: string;
  hostname?: string;
  generatedAt: string;
}

export interface RegistrationRequestIdentityRecord {
  type: string;
  version: number;
  requestId: string;
  appId: string;
  installationId: string;
  softwareName?: string;
  softwareVersion?: string;
  hostname?: string;
  generatedAt: string;
  expiresAt: string;
}

export interface RegistrationRequestRecord {
  id: string;
  appId: string;
  installationId: string;
  hostname?: string;
  os: string;
  arch: string;
  currentVersion: string;
  releaseChannel: string;
  status: RegistrationRequestStatus;
  clientId?: number;
  approvedByEmployeeId?: string;
  approvedAt?: string;
  rejectedByEmployeeId?: string;
  rejectedAt?: string;
  rejectReason?: string;
  stationNote?: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ResolveRegistrationRequestResponse {
  identity: RegistrationRequestIdentityRecord;
  request: RegistrationRequestRecord;
}

export interface RegistrationApprovalConfirmation {
  locator: string;
  confirmedRequestId: string;
  confirmedAppId: string;
  confirmedClientId: number;
}

export interface LicensingStationRecord {
  id: string;
  clientId: number;
  appId: string;
  installationId: string;
  hostname?: string;
  fingerprintHash?: string;
  enrolledAt: string;
  enrolledByEmployeeId: string;
  currentVersion: string;
  os: string;
  arch: string;
  releaseChannel: string;
  lastSeenAt?: string;
  lastSeenIp?: string;
  status: string;
  decommissionedAt?: string;
  minSupportedVersion?: string;
  outdated?: boolean;
  adminBan?: StationAdminControlRecord;
  adminAllow?: StationAdminControlRecord;
  policy?: {
    notes?: string;
    releaseChannelOverride?: string;
    thresholdDaysOverride?: number;
    warningDaysOverride?: number;
    offlineGraceDaysOverride?: number;
    readonlyGraceDaysOverride?: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface ResolveInstallationResponse {
  status: InstallationResolveStatus;
  identity: InstallationIdentityRecord;
  station?: LicensingStationRecord;
}

export interface IssuedEnrollmentTokenRecord {
  token: string;
  appId: string;
  clientId: number;
  expiresAt: string;
}

export interface LicensingPublicKeyRecord {
  kind: string;
  version: number;
  publicKey: string;
}

export interface StationAdminControlRecord {
  active: boolean;
  reason?: string;
  appliedByEmployeeId?: string;
  appliedAt?: string;
  validUntil?: string;
}

export interface StationOverrideRecord {
  id: string;
  stationId: string;
  issuedByEmployeeId: string;
  issuedAt: string;
  validUntil: string;
  reason: string;
  status: string;
  createdAt?: string;
}

export interface StationCertRecord {
  id: string;
  stationId: string;
  issuedAt: string;
  notBefore: string;
  notAfter: string;
  signingKeyVersion: number;
  payloadCanonical?: unknown;
  revokedAt?: string;
  revokedReason?: string;
}

export interface StationAuditRecord {
  id: string;
  stationId?: string;
  at: string;
  event: string;
  payload?: unknown;
  initiatedByEmployeeId?: string;
  appId?: string;
  clientId?: number;
}

export interface StationLicenseStateRecord {
  stationId: string;
  appId: string;
  clientId: number;
  stationStatus: string;
  lastSeenAt?: string | null;
  evaluatedAt: string;
  effectiveAllowed: boolean;
  reason: string;
  entitlementStatus?: string;
  entitlementCheckedAt?: string;
  entitlementValidUntil?: string | null;
  entitlementWarningStarts?: string | null;
  entitlementDecisionRef?: string;
  cert?: {
    id: string;
    issuedAt: string;
    notAfter: string;
    expired: boolean;
    signingKeyVersion: number;
  };
  override?: {
    id: string;
    issuedAt: string;
    validUntil: string;
    issuedByEmployeeId: string;
    status: string;
  };
  adminBan?: StationAdminControlRecord;
  adminAllow?: StationAdminControlRecord;
  certCarriesActiveOverride: boolean;
  requiresRefresh: boolean;
}

export type LicensingE2EEntitlementAction = 'ok' | 'suspended' | 'canonical';

export interface LicensingE2EEntitlementView {
  capability: true;
  clientId: number;
  canonicalStatus: 'ok' | 'warning' | 'suspended';
  effectiveStatus: 'ok' | 'warning' | 'suspended';
  source: 'canonical' | 'dev_e2e';
  controlHealth: 'absent' | 'valid' | 'malformed';
  revision: string | null;
  evaluatedAt: string;
}

export interface ListResponse<T> {
  items: T[];
}

export interface ReleaseArtifactRecord {
  id: string;
  releaseId: string;
  os: string;
  arch: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  signingKeyVersion: number;
  signature: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReleaseDownloadCatalogItemRecord {
  releaseId: string;
  appId: string;
  version: string;
  channel: string;
  title: string;
  notes: string;
  publishedAt?: string;
  artifact: ReleaseArtifactRecord;
}

export interface ReleaseDownloadGrantRecord {
  id: string;
  artifactId: string;
  clientId?: number;
  createdByEmployeeId: string;
  createdAt: string;
  expiresAt: string;
  maxDownloads: number;
  downloadCount: number;
  lastDownloadedAt?: string;
  status: string;
  effectiveStatus: string;
  note?: string;
  revokedAt?: string;
  revokedByEmployeeId?: string;
  revokedReason?: string;
}

export interface IssuedReleaseDownloadGrantRecord extends ReleaseDownloadGrantRecord {
  artifact: ReleaseArtifactRecord;
  downloadToken: string;
  downloadUrl: string;
}

export interface GuestDownloadRequestRecord {
  id: string;
  requestCode: string;
  status: GuestDownloadRequestStatus;
  clientId?: number;
  approvedByEmployeeId?: string;
  approvedAt?: string;
  revokedByEmployeeId?: string;
  revokedAt?: string;
  revokedReason?: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApprovedGuestDownloadRequestRecord extends GuestDownloadRequestRecord {
  artifacts: ReleaseDownloadCatalogItemRecord[];
}

export interface ReleaseDownloadCatalogFilters {
  appId?: string;
  channel?: string;
  os?: string;
  arch?: string;
}

export interface ReleaseDownloadGrantFilters {
  appId?: string;
  artifactId?: string;
  status?: string;
}

export interface GuestDownloadRequestFilters {
  status?: GuestDownloadRequestStatus;
  search?: string;
  limit?: number;
  offset?: number;
}

export const licensingApi = {
  resolveInstallation(payload: string): Promise<ResolveInstallationResponse> {
    return api
      .post<ResolveInstallationResponse>('/licensing/mobile/installations/resolve', { payload })
      .then((response) => response.data);
  },

  resolveRegistrationRequest(
    payload: string,
    clientId?: number,
  ): Promise<ResolveRegistrationRequestResponse> {
    return api
      .post<ResolveRegistrationRequestResponse>(
        '/licensing/mobile/registration-requests/resolve',
        { payload, ...(clientId ? { clientId } : {}) },
      )
      .then((response) => response.data);
  },

  approveRegistrationRequest(
    requestId: string,
    clientId: number,
    confirmation: RegistrationApprovalConfirmation,
    stationNote?: string,
  ): Promise<RegistrationRequestRecord> {
    return api
      .post<RegistrationRequestRecord>(`/licensing/mobile/registration-requests/${encodeURIComponent(requestId)}/approve`, {
        clientId,
        stationNote,
        ...confirmation,
      })
      .then((response) => response.data);
  },

  approveClientRegistrationRequest(
    requestId: string,
    clientId: number,
    confirmation: RegistrationApprovalConfirmation,
    stationNote?: string,
  ): Promise<RegistrationRequestRecord> {
    return api
      .post<RegistrationRequestRecord>(`/licensing/mobile/clients/${clientId}/registration-requests/${encodeURIComponent(requestId)}/approve`, {
        stationNote,
        ...confirmation,
      })
      .then((response) => response.data);
  },

  rejectRegistrationRequest(requestId: string, reason: string): Promise<RegistrationRequestRecord> {
    return api
      .post<RegistrationRequestRecord>(`/licensing/mobile/registration-requests/${encodeURIComponent(requestId)}/reject`, { reason })
      .then((response) => response.data);
  },

  issueEnrollmentToken(appId: string, clientId: number): Promise<IssuedEnrollmentTokenRecord> {
    return api
      .post<IssuedEnrollmentTokenRecord>('/licensing/mobile/enrollment-tokens', {
        appId,
        clientId,
        ttl: '1h',
      })
      .then((response) => response.data);
  },

  getPublicKey(): Promise<LicensingPublicKeyRecord> {
    return api
      .get<LicensingPublicKeyRecord>('/licensing/mobile/keys/public')
      .then((response) => response.data);
  },

  listClientStations(clientId: number): Promise<ListResponse<LicensingStationRecord>> {
    return api
      .get<ListResponse<LicensingStationRecord>>(`/licensing/mobile/clients/${clientId}/stations`)
      .then((response) => response.data);
  },

  getClientE2EEntitlement(clientId: number): Promise<LicensingE2EEntitlementView> {
    return api
      .get<LicensingE2EEntitlementView>(
        `/licensing/mobile/clients/${clientId}/e2e-entitlement`,
      )
      .then((response) => response.data);
  },

  setClientE2EEntitlement(
    clientId: number,
    status: LicensingE2EEntitlementAction,
    reason: string,
  ): Promise<LicensingE2EEntitlementView> {
    return api
      .put<LicensingE2EEntitlementView>(
        `/licensing/mobile/clients/${clientId}/e2e-entitlement`,
        { status, reason },
      )
      .then((response) => response.data);
  },

  listReleaseDownloadCatalog(filters?: ReleaseDownloadCatalogFilters): Promise<ListResponse<ReleaseDownloadCatalogItemRecord>> {
    return api
      .get<ListResponse<ReleaseDownloadCatalogItemRecord>>('/licensing/mobile/release-download-catalog', { params: filters })
      .then((response) => response.data);
  },

  listClientReleaseDownloadCatalog(
    clientId: number,
    filters?: ReleaseDownloadCatalogFilters
  ): Promise<ListResponse<ReleaseDownloadCatalogItemRecord>> {
    return api
      .get<ListResponse<ReleaseDownloadCatalogItemRecord>>(`/licensing/mobile/clients/${clientId}/release-download-catalog`, { params: filters })
      .then((response) => response.data);
  },

  listClientReleaseDownloadGrants(
    clientId: number,
    filters?: ReleaseDownloadGrantFilters
  ): Promise<ListResponse<ReleaseDownloadGrantRecord>> {
    return api
      .get<ListResponse<ReleaseDownloadGrantRecord>>(`/licensing/mobile/clients/${clientId}/release-download-grants`, { params: filters })
      .then((response) => response.data);
  },

  createClientReleaseDownloadGrant(
    clientId: number,
    input: {
      artifactId: string;
      ttlHours?: number;
      maxDownloads?: number;
      note?: string;
    }
  ): Promise<IssuedReleaseDownloadGrantRecord> {
    return api
      .post<IssuedReleaseDownloadGrantRecord>(`/licensing/mobile/clients/${clientId}/release-download-grants`, input)
      .then((response) => response.data);
  },

  resolveGuestDownloadRequest(requestCode: string): Promise<GuestDownloadRequestRecord> {
    return api
      .get<GuestDownloadRequestRecord>(`/licensing/mobile/guest-download-requests/${encodeURIComponent(requestCode)}`)
      .then((response) => response.data);
  },

  listGuestDownloadRequests(filters?: GuestDownloadRequestFilters): Promise<ListResponse<GuestDownloadRequestRecord>> {
    return api
      .get<ListResponse<GuestDownloadRequestRecord>>('/licensing/mobile/guest-download-requests', { params: filters })
      .then((response) => response.data);
  },

  approveGuestDownloadRequest(
    requestCode: string,
    input: {
      clientId: number;
      artifactIds: string[];
      ttlHours?: number;
    }
  ): Promise<ApprovedGuestDownloadRequestRecord> {
    return api
      .post<ApprovedGuestDownloadRequestRecord>(
        `/licensing/mobile/guest-download-requests/${encodeURIComponent(requestCode)}/approve`,
        input
      )
      .then((response) => response.data);
  },

  approveClientGuestDownloadRequest(
    requestCode: string,
    clientId: number,
    input: {
      artifactIds: string[];
      ttlHours?: number;
    }
  ): Promise<ApprovedGuestDownloadRequestRecord> {
    return api
      .post<ApprovedGuestDownloadRequestRecord>(
        `/licensing/mobile/clients/${clientId}/guest-download-requests/${encodeURIComponent(requestCode)}/approve`,
        input
      )
      .then((response) => response.data);
  },

  revokeGuestDownloadRequest(requestCode: string, reason: string): Promise<GuestDownloadRequestRecord> {
    return api
      .post<GuestDownloadRequestRecord>(
        `/licensing/mobile/guest-download-requests/${encodeURIComponent(requestCode)}/revoke`,
        { reason }
      )
      .then((response) => response.data);
  },

  revokeClientReleaseDownloadGrant(
    clientId: number,
    grantId: string,
    reason: string
  ): Promise<ReleaseDownloadGrantRecord> {
    return api
      .post<ReleaseDownloadGrantRecord>(`/licensing/mobile/clients/${clientId}/release-download-grants/${encodeURIComponent(grantId)}/revoke`, { reason })
      .then((response) => response.data);
  },

  getStation(stationId: string): Promise<LicensingStationRecord> {
    return api
      .get<LicensingStationRecord>(`/licensing/mobile/stations/${encodeURIComponent(stationId)}`)
      .then((response) => response.data);
  },

  getStationLicenseState(stationId: string): Promise<StationLicenseStateRecord> {
    return api
      .get<StationLicenseStateRecord>(`/licensing/mobile/stations/${encodeURIComponent(stationId)}/license-state`)
      .then((response) => response.data);
  },

  listStationCerts(stationId: string): Promise<ListResponse<StationCertRecord>> {
    return api
      .get<ListResponse<StationCertRecord>>(`/licensing/mobile/stations/${encodeURIComponent(stationId)}/certs?limit=10`)
      .then((response) => response.data);
  },

  listStationOverrides(stationId: string): Promise<ListResponse<StationOverrideRecord>> {
    return api
      .get<ListResponse<StationOverrideRecord>>(`/licensing/mobile/stations/${encodeURIComponent(stationId)}/overrides?limit=10`)
      .then((response) => response.data);
  },

  listStationAudit(stationId: string): Promise<ListResponse<StationAuditRecord>> {
    return api
      .get<ListResponse<StationAuditRecord>>(`/licensing/mobile/stations/${encodeURIComponent(stationId)}/audit?limit=20`)
      .then((response) => response.data);
  },

  issueStationOverride(stationId: string, reason: string): Promise<StationOverrideRecord> {
    return api
      .post<StationOverrideRecord>(`/licensing/mobile/stations/${encodeURIComponent(stationId)}/overrides`, { reason })
      .then((response) => response.data);
  },

  decommissionStation(stationId: string, reason: string): Promise<LicensingStationRecord> {
    return api
      .post<LicensingStationRecord>(`/licensing/mobile/stations/${encodeURIComponent(stationId)}/decommission`, { reason })
      .then((response) => response.data);
  },

  updateStationNote(stationId: string, notes: string, reason: string): Promise<LicensingStationRecord> {
    return api
      .patch<LicensingStationRecord>(`/licensing/mobile/stations/${encodeURIComponent(stationId)}/note`, {
        notes,
        reason,
      })
      .then((response) => response.data);
  },
};
